import { getPortfolioPlannerScopes, type PortfolioPlannerScopesResponse } from './portfolioPlannerApi';
import { getActiveStrategy, getStrategy } from './strategyStudioV2Api';
import { formatScopeRootNodeIdForUi } from './uiIdLabel';

type LeadScope = PortfolioPlannerScopesResponse['leadScopes'][number];

export type OperatingContextReady = {
  kind: 'ready';
  strategyId: string;
  strategyTitle: string | null;
  coverage:
    | {
        kind: 'business_area';
        scopeRootNodeId: string;
        label: string;
      }
    | {
        kind: 'enterprise_wide';
        scopeRootNodeId: null;
        label: string;
      };
  leadScopes: PortfolioPlannerScopesResponse['leadScopes'];
};

export type OperatingContextUnavailable = {
  kind: 'unavailable';
  strategyId: string | null;
  strategyTitle: string | null;
  coverage: {
    kind: 'unavailable';
    scopeRootNodeId: null;
    label: string;
  };
  reason: string;
  leadScopes: PortfolioPlannerScopesResponse['leadScopes'];
};

export type OperatingContext = OperatingContextReady | OperatingContextUnavailable;

export type OperatingContextOverrides = {
  strategyId?: string | null;
  scopeRootNodeId?: string | null;
};

function normalizeOptionalId(value: unknown): string | null {
  const trimmed = String(value ?? '').trim();
  return trimmed ? trimmed : null;
}

function pluralizeSeatCount(count: number): string {
  const normalized = Math.max(0, Math.floor(count));
  return `${normalized} seat${normalized === 1 ? '' : 's'}`;
}

export function buildOperatingCoverageLabel(params: { scopeRootNodeId: string; leadScope: LeadScope | null }): string {
  const lead = params.leadScope ?? null;
  const scopeRootNodeId = String(params.scopeRootNodeId ?? '').trim();

  const personName = String(lead?.personName ?? '').trim();
  const title = String(lead?.title ?? '').trim();
  const unitName = String(lead?.unitName ?? '').trim();

  const headcountRaw = Number(lead?.headcount ?? NaN);
  const headcount = Number.isFinite(headcountRaw) ? Math.max(0, Math.floor(headcountRaw)) : 0;
  const countLabel = headcount > 0 ? pluralizeSeatCount(headcount) : 'seats unknown';

  const identityLabel =
    personName && title
      ? `${personName} (${title})`
      : personName
        ? personName
        : title
          ? title
          : unitName
            ? unitName
            : formatScopeRootNodeIdForUi(scopeRootNodeId) || scopeRootNodeId;

  return `${identityLabel} · ${countLabel}`;
}

export async function loadOperatingContext(overrides: OperatingContextOverrides = {}): Promise<OperatingContext> {
  let strategyId: string | null = normalizeOptionalId(overrides.strategyId);
  let strategyTitle: string | null = null;

  if (!strategyId) {
    try {
      const active = await getActiveStrategy();
      strategyId = String(active?.strategyId ?? '').trim() || null;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load active enterprise strategy.';
      const isMissingSession = message.toLowerCase().includes('missing session');
      return {
        kind: 'unavailable',
        strategyId: null,
        strategyTitle: null,
        coverage: {
          kind: 'unavailable',
          scopeRootNodeId: null,
          label: 'Operating coverage unavailable',
        },
        reason: isMissingSession
          ? 'Your API session is missing. Make sure you are logged in at http://127.0.0.1:3100 (not localhost), then refresh.'
          : `Could not load the active enterprise strategy. ${message}`,
        leadScopes: [],
      };
    }
  }

  if (!strategyId) {
    return {
      kind: 'unavailable',
      strategyId: null,
      strategyTitle: null,
      coverage: {
        kind: 'unavailable',
        scopeRootNodeId: null,
        label: 'Operating coverage unavailable',
      },
      reason: 'No active enterprise strategy is set.',
      leadScopes: [],
    };
  }

  try {
    const strategy = await getStrategy(strategyId);
    const title = typeof strategy?.title === 'string' ? strategy.title : null;
    strategyTitle = String(title ?? '').trim() || null;
  } catch {
    strategyTitle = null;
  }

  try {
    const scopes = await getPortfolioPlannerScopes();
    const leadScopes = Array.isArray(scopes?.leadScopes) ? scopes.leadScopes : [];
    const defaultViewKind = scopes?.defaultView?.kind;
    const requestedScopeRootNodeId = normalizeOptionalId(overrides.scopeRootNodeId);

    if (requestedScopeRootNodeId) {
      const matchingLeadScope =
        leadScopes.find((item) => String(item?.scopeRootNodeId ?? '').trim() === requestedScopeRootNodeId) ?? null;

      return {
        kind: 'ready',
        strategyId,
        strategyTitle,
        coverage: {
          kind: 'business_area',
          scopeRootNodeId: requestedScopeRootNodeId,
          label: buildOperatingCoverageLabel({ scopeRootNodeId: requestedScopeRootNodeId, leadScope: matchingLeadScope }),
        },
        leadScopes,
      };
    }

    if (defaultViewKind === 'enterprise_rollup') {
      return {
        kind: 'ready',
        strategyId,
        strategyTitle,
        coverage: {
          kind: 'enterprise_wide',
          scopeRootNodeId: null,
          label: 'Enterprise-wide',
        },
        leadScopes,
      };
    }

    if (defaultViewKind === 'blocked') {
      return {
        kind: 'unavailable',
        strategyId,
        strategyTitle,
        coverage: {
          kind: 'unavailable',
          scopeRootNodeId: null,
          label: 'Operating coverage unavailable',
        },
        reason: String(scopes.defaultView.reason ?? '').trim() || 'Operating coverage is not assigned.',
        leadScopes,
      };
    }

    const scopeRootNodeId = String(scopes?.defaultView?.scopeRootNodeId ?? '').trim();
    if (!scopeRootNodeId) {
      return {
        kind: 'unavailable',
        strategyId,
        strategyTitle,
        coverage: {
          kind: 'unavailable',
          scopeRootNodeId: null,
          label: 'Operating coverage unavailable',
        },
        reason: 'Operating coverage is not assigned.',
        leadScopes,
      };
    }

    const matchingLeadScope = leadScopes.find((item) => String(item?.scopeRootNodeId ?? '').trim() === scopeRootNodeId) ?? null;

    return {
      kind: 'ready',
      strategyId,
      strategyTitle,
      coverage: {
        kind: 'business_area',
        scopeRootNodeId,
        label: buildOperatingCoverageLabel({ scopeRootNodeId, leadScope: matchingLeadScope }),
      },
      leadScopes,
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to load operating coverage.';
    return {
      kind: 'unavailable',
      strategyId,
      strategyTitle,
      coverage: {
        kind: 'unavailable',
        scopeRootNodeId: null,
        label: 'Operating coverage unavailable',
      },
      reason: `Could not load operating coverage. ${message}`,
      leadScopes: [],
    };
  }
}
